<template>
  <div class="p-6 bg-gray-100 min-h-screen">
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">积分排名</h1>
        <div class="flex space-x-4">
          <select 
            v-model="departmentFilter" 
            class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">全部部门</option>
            <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
          </select>
          <select 
            v-model="timeFilter" 
            class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="month">本月</option>
            <option value="quarter">本季度</option>
            <option value="year">今年</option>
            <option value="all">全部时间</option>
          </select>
          <select 
            v-model="categoryFilter" 
            class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">全部类别</option>
            <option value="1">政治素养</option>
            <option value="2">专业素养</option>
            <option value="3">考评及荣誉</option>
          </select>
        </div>
      </div>

      <!-- 积分排名统计图表 -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow col-span-2 p-4">
          <h3 class="text-lg font-semibold mb-4 text-gray-700">部门积分分布</h3>
          <div class="h-64 bg-gray-50 rounded" ref="departmentChart">
            <!-- ECharts将在这里渲染 -->
          </div>
        </div>
        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-lg font-semibold mb-4 text-gray-700">积分类别分布</h3>
          <div class="h-64 bg-gray-50 rounded" ref="categoryChart">
            <!-- ECharts将在这里渲染 -->
          </div>
        </div>
      </div>

      <!-- 排名前三卡片 -->
      <div class="mb-8">
        <h3 class="text-lg font-semibold mb-4 text-gray-700">排名前三</h3>
        <div class="flex flex-wrap gap-6">
          <!-- 第一名 -->
          <div v-if="topThree && topThree.length > 0" class="bg-white rounded-lg shadow-lg border border-yellow-300 p-6 flex-1 min-w-[250px] relative overflow-hidden">
            <div class="absolute top-0 right-0 bg-yellow-400 text-white px-3 py-1 transform rotate-45 translate-x-8 -translate-y-1 shadow-md">
              <span class="text-sm font-bold">1st</span>
            </div>
            <div class="flex items-center">
              <div class="w-16 h-16 rounded-full bg-yellow-100 text-yellow-600 border-2 border-yellow-400 mr-4 flex items-center justify-center">
                <span class="text-2xl font-semibold">{{ topThree[0].name.substring(0, 1) }}</span>
              </div>
              <div>
                <p class="text-xl font-bold text-gray-800">{{ topThree[0].name }}</p>
                <p class="text-sm text-gray-600">{{ topThree[0].department }}</p>
              </div>
              <div class="ml-auto">
                <p class="text-3xl font-bold text-yellow-500">{{ topThree[0].total }}</p>
                <p class="text-sm text-gray-500 text-right">积分</p>
              </div>
            </div>
            <div class="mt-4 pt-4 border-t border-gray-100">
              <div class="flex justify-between text-sm">
                <p class="text-gray-500">政治素养: <span class="text-gray-700 font-medium">{{ topThree[0].political }}</span></p>
                <p class="text-gray-500">专业素养: <span class="text-gray-700 font-medium">{{ topThree[0].professional }}</span></p>
                <p class="text-gray-500">考评荣誉: <span class="text-gray-700 font-medium">{{ topThree[0].honor }}</span></p>
              </div>
            </div>
          </div>
          
          <!-- 第二名 -->
          <div v-if="topThree && topThree.length > 1" class="bg-white rounded-lg shadow-lg border border-gray-300 p-6 flex-1 min-w-[250px] relative overflow-hidden">
            <div class="absolute top-0 right-0 bg-gray-400 text-white px-3 py-1 transform rotate-45 translate-x-8 -translate-y-1 shadow-md">
              <span class="text-sm font-bold">2nd</span>
            </div>
            <div class="flex items-center">
              <div class="w-16 h-16 rounded-full bg-gray-100 text-gray-600 border-2 border-gray-400 mr-4 flex items-center justify-center">
                <span class="text-2xl font-semibold">{{ topThree[1].name.substring(0, 1) }}</span>
              </div>
              <div>
                <p class="text-xl font-bold text-gray-800">{{ topThree[1].name }}</p>
                <p class="text-sm text-gray-600">{{ topThree[1].department }}</p>
              </div>
              <div class="ml-auto">
                <p class="text-3xl font-bold text-gray-500">{{ topThree[1].total }}</p>
                <p class="text-sm text-gray-500 text-right">积分</p>
              </div>
            </div>
            <div class="mt-4 pt-4 border-t border-gray-100">
              <div class="flex justify-between text-sm">
                <p class="text-gray-500">政治素养: <span class="text-gray-700 font-medium">{{ topThree[1].political }}</span></p>
                <p class="text-gray-500">专业素养: <span class="text-gray-700 font-medium">{{ topThree[1].professional }}</span></p>
                <p class="text-gray-500">考评荣誉: <span class="text-gray-700 font-medium">{{ topThree[1].honor }}</span></p>
              </div>
            </div>
          </div>
          
          <!-- 第三名 -->
          <div v-if="topThree && topThree.length > 2" class="bg-white rounded-lg shadow-lg border border-yellow-700 p-6 flex-1 min-w-[250px] relative overflow-hidden">
            <div class="absolute top-0 right-0 bg-yellow-700 text-white px-3 py-1 transform rotate-45 translate-x-8 -translate-y-1 shadow-md">
              <span class="text-sm font-bold">3rd</span>
            </div>
            <div class="flex items-center">
              <div class="w-16 h-16 rounded-full bg-yellow-100 text-yellow-700 border-2 border-yellow-700 mr-4 flex items-center justify-center">
                <span class="text-2xl font-semibold">{{ topThree[2].name.substring(0, 1) }}</span>
              </div>
              <div>
                <p class="text-xl font-bold text-gray-800">{{ topThree[2].name }}</p>
                <p class="text-sm text-gray-600">{{ topThree[2].department }}</p>
              </div>
              <div class="ml-auto">
                <p class="text-3xl font-bold text-yellow-700">{{ topThree[2].total }}</p>
                <p class="text-sm text-gray-500 text-right">积分</p>
              </div>
            </div>
            <div class="mt-4 pt-4 border-t border-gray-100">
              <div class="flex justify-between text-sm">
                <p class="text-gray-500">政治素养: <span class="text-gray-700 font-medium">{{ topThree[2].political }}</span></p>
                <p class="text-gray-500">专业素养: <span class="text-gray-700 font-medium">{{ topThree[2].professional }}</span></p>
                <p class="text-gray-500">考评荣誉: <span class="text-gray-700 font-medium">{{ topThree[2].honor }}</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 排名列表 -->
      <div class="overflow-hidden shadow-md border border-gray-200 rounded-lg">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">排名</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">员工</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">部门</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">政治素养</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">专业素养</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">考评荣誉</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">总积分</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(item, index) in filteredRankings" :key="index" 
              :class="[
                'hover:bg-gray-50 transition-colors duration-200',
                index < 3 ? 'bg-gray-50' : ''
              ]"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <span 
                    v-if="index < 3" 
                    :class="[
                      'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white',
                      index === 0 ? 'bg-yellow-400' : index === 1 ? 'bg-gray-400' : 'bg-yellow-700'
                    ]"
                  >
                    {{ index + 1 }}
                  </span>
                  <span v-else class="text-gray-500">{{ index + 1 }}</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-red-100 text-red-600 mr-3 flex items-center justify-center">
                    {{ item.initials }}
                  </div>
                  {{ item.name }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.department }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.political }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.professional }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.honor }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-semibold" 
                :class="[
                  index === 0 ? 'text-yellow-500' : 
                  index === 1 ? 'text-gray-500' : 
                  index === 2 ? 'text-yellow-700' : 'text-gray-900'
                ]"
              >
                {{ item.total }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <button @click="viewDetail(item)" class="text-blue-600 hover:text-blue-800">
                  详情
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="flex justify-between items-center mt-6">
        <p class="text-sm text-gray-700">
          显示 <span class="font-medium">{{ (currentPage - 1) * pageSize + 1 }}</span> 至 <span class="font-medium">{{ Math.min(currentPage * pageSize, totalItems) }}</span> 条，共 <span class="font-medium">{{ totalItems }}</span> 条
        </p>
        <div class="flex space-x-2">
          <button 
            @click="previousPage" 
            :disabled="currentPage === 1" 
            :class="[
              'px-3 py-1 border rounded-md',
              currentPage === 1 ? 'cursor-not-allowed text-gray-400 border-gray-200' : 'border-gray-300 text-gray-700'
            ]"
          >
            上一页
          </button>
          <button 
            v-for="page in Math.min(5, totalPages)" 
            :key="page" 
            @click="goToPage(page)" 
            :class="[
              'px-3 py-1 border rounded-md', 
              currentPage === page ? 'bg-red-500 text-white border-red-500' : 'border-gray-300 text-gray-700'
            ]"
          >
            {{ page }}
          </button>
          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages" 
            :class="[
              'px-3 py-1 border rounded-md',
              currentPage === totalPages ? 'cursor-not-allowed text-gray-400 border-gray-200' : 'border-gray-300 text-gray-700'
            ]"
          >
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 员工积分详情对话框 -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <!-- 对话框头部 -->
        <div class="flex justify-between items-center border-b px-6 py-4">
          <h3 class="text-lg font-semibold text-gray-800">积分详情</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-500">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <!-- 对话框内容 -->
        <div class="px-6 py-4">
          <div v-if="selectedEmployee">
            <div class="bg-gray-50 p-4 rounded-lg mb-6 flex items-center">
              <div class="w-16 h-16 rounded-full bg-red-100 text-red-600 mr-4 flex items-center justify-center text-2xl">
                {{ selectedEmployee.initials }}
              </div>
              <div>
                <p class="text-xl font-semibold text-gray-800">{{ selectedEmployee.name }}</p>
                <p class="text-sm text-gray-500">{{ selectedEmployee.department }}</p>
              </div>
              <div class="ml-auto text-center">
                <p class="text-3xl font-bold text-red-500">{{ selectedEmployee.total }}</p>
                <p class="text-sm text-gray-500">总积分</p>
              </div>
            </div>

            <!-- 积分分类统计 -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div class="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <p class="text-sm text-blue-500 font-medium">政治素养</p>
                <p class="text-2xl font-bold text-blue-600">{{ selectedEmployee.political }}</p>
                <div class="mt-2 bg-blue-200 h-1.5 rounded-full overflow-hidden">
                  <div class="bg-blue-500 h-full" :style="`width: ${(selectedEmployee.political / selectedEmployee.total) * 100}%`"></div>
                </div>
              </div>
              <div class="bg-green-50 rounded-lg p-4 border border-green-100">
                <p class="text-sm text-green-500 font-medium">专业素养</p>
                <p class="text-2xl font-bold text-green-600">{{ selectedEmployee.professional }}</p>
                <div class="mt-2 bg-green-200 h-1.5 rounded-full overflow-hidden">
                  <div class="bg-green-500 h-full" :style="`width: ${(selectedEmployee.professional / selectedEmployee.total) * 100}%`"></div>
                </div>
              </div>
              <div class="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <p class="text-sm text-purple-500 font-medium">考评荣誉</p>
                <p class="text-2xl font-bold text-purple-600">{{ selectedEmployee.honor }}</p>
                <div class="mt-2 bg-purple-200 h-1.5 rounded-full overflow-hidden">
                  <div class="bg-purple-500 h-full" :style="`width: ${(selectedEmployee.honor / selectedEmployee.total) * 100}%`"></div>
                </div>
              </div>
            </div>

            <!-- 积分获取历史 -->
            <h4 class="font-medium text-gray-700 mb-3">最近积分记录</h4>
            <div class="border rounded-lg overflow-hidden">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">积分项目</th>
                    <th scope="col" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">类别</th>
                    <th scope="col" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">积分值</th>
                    <th scope="col" class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">获得时间</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(record, idx) in selectedEmployee.records" :key="idx" class="hover:bg-gray-50">
                    <td class="px-4 py-3 text-sm text-gray-900">{{ record.name }}</td>
                    <td class="px-4 py-3 text-sm text-gray-600">{{ record.category }}</td>
                    <td class="px-4 py-3 text-sm font-medium" :class="getScoreColor(record.category)">
                      +{{ record.score }}
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-500">{{ record.date }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getScoreRanking, getDepartments, getTopThree, getScoreDistribution } from '@/api/score';
import message from '@/utils/message';
import * as echarts from 'echarts';

export default {
  name: 'ScoreRanking',
  data() {
    return {
      departmentFilter: 'all',
      timeFilter: 'month',
      categoryFilter: 'all',
      departments: [],
      rankings: [],
      topThree: [],
      distributionData: {},
      loading: false,
      currentPage: 1,
      pageSize: 20,
      totalItems: 0,
      showModal: false,
      selectedEmployee: null,
      departmentChart: null,
      categoryChart: null
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
    filteredRankings() {
      return this.rankings;
    }
  },
  created() {
    console.log('ScoreRanking组件创建，开始加载数据');
    // 首先加载基础数据
    this.fetchDepartments();
    this.fetchTopThree();
    this.fetchScoreRanking();
    this.fetchDistributionData();
  },
  watch: {
    departmentFilter() {
      console.log('部门筛选变化:', this.departmentFilter);
      this.currentPage = 1; // 重置到第一页
      this.fetchTopThree();
      this.fetchScoreRanking();
      this.fetchDistributionData();
    },
    timeFilter() {
      console.log('时间筛选变化:', this.timeFilter);
      this.currentPage = 1; // 重置到第一页
      this.fetchTopThree();
      this.fetchScoreRanking();
      this.fetchDistributionData();
    },
    categoryFilter() {
      console.log('类别筛选变化:', this.categoryFilter);
      this.currentPage = 1; // 重置到第一页
      this.fetchTopThree();
      this.fetchScoreRanking();
      this.fetchDistributionData();
    },
    currentPage() {
      console.log('页码变化:', this.currentPage);
      this.fetchScoreRanking();
    }
  },
  mounted() {
    console.log('ScoreRanking组件挂载，初始化图表');
    // 初始化图表
    this.$nextTick(() => {
      this.initCharts();
    });
    
    // 添加窗口大小变化监听器以实现响应式调整
    window.addEventListener('resize', this.resizeCharts);
  },
  beforeDestroy() {
    // 移除事件监听器
    window.removeEventListener('resize', this.resizeCharts);
    
    // 销毁图表实例
    if (this.departmentChart) {
      this.departmentChart.dispose();
      this.departmentChart = null;
    }
    if (this.categoryChart) {
      this.categoryChart.dispose();
      this.categoryChart = null;
    }
  },
  methods: {
    fetchDepartments() {
      this.loading = true;
      getDepartments()
        .then(response => {
          if (response.code === 200) {
            this.departments = response.data || [];
          } else {
            message.error(response.message || '获取部门列表失败');
          }
        })
        .catch(error => {
          console.error('获取部门列表失败:', error);
          message.error('获取部门列表失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchTopThree() {
      this.loading = true;
      getTopThree({
        dept: this.departmentFilter === 'all' ? '' : this.departmentFilter,
        timeRange: this.timeFilter,
        category: this.categoryFilter === 'all' ? '' : this.categoryFilter
      })
        .then(response => {
          if (response.code === 200) {
            this.topThree = response.data || [];
            console.log('获取排名前三成功:', this.topThree); // 添加日志便于调试
          } else {
            message.error(response.message || '获取前三名数据失败');
          }
        })
        .catch(error => {
          console.error('获取前三名数据失败:', error);
          message.error('获取前三名数据失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchScoreRanking() {
      this.loading = true;
      getScoreRanking({
        dept: this.departmentFilter === 'all' ? '' : this.departmentFilter,
        timeRange: this.timeFilter,
        category: this.categoryFilter === 'all' ? '' : this.categoryFilter,
        page: this.currentPage,
        size: this.pageSize
      })
        .then(response => {
          if (response.code === 200) {
            this.rankings = response.data.list || [];
            this.totalItems = response.data.total || 0;
            console.log('获取排名列表成功:', this.rankings.length); // 添加日志便于调试
          } else {
            message.error(response.message || '获取排名数据失败');
          }
        })
        .catch(error => {
          console.error('获取排名数据失败:', error);
          message.error('获取排名数据失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchDistributionData() {
      this.loading = true;
      getScoreDistribution({
        dept: this.departmentFilter === 'all' ? '' : this.departmentFilter,
        timeRange: this.timeFilter,
        type: 'department'
      })
        .then(response => {
          if (response.code === 200) {
            this.distributionData = response.data || {};
            // 获取数据后更新图表
            this.$nextTick(() => {
              this.updateCharts();
            });
          } else {
            message.error(response.message || '获取分布数据失败');
          }
        })
        .catch(error => {
          console.error('获取分布数据失败:', error);
          message.error('获取分布数据失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    viewDetail(item) {
      this.selectedEmployee = { ...item }; // 创建副本，避免直接修改原始数据
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedEmployee = null;
    },
    getScoreColor(category) {
      if (category === '政治素养') return 'text-blue-600';
      if (category === '专业素养') return 'text-green-600';
      if (category === '考评荣誉') return 'text-purple-600';
      return 'text-gray-700';
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchScoreRanking();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchScoreRanking();
      }
    },
    goToPage(page) {
      this.currentPage = page;
      this.fetchScoreRanking();
    },
    exportRankings() {
      message.info('导出功能正在开发中...');
    },
    initCharts() {
      // 检查DOM元素是否存在
      if (!this.$refs.departmentChart || !this.$refs.categoryChart) {
        console.error('图表DOM元素不存在，将在下一次更新时重试');
        // 安排下一次DOM更新后再次尝试初始化
        this.$nextTick(this.initCharts);
        return;
      }
      
      try {
        // 初始化部门积分分布图表
        this.departmentChart = echarts.init(this.$refs.departmentChart);
        console.log('部门积分分布图表初始化成功');
        
        // 初始化积分类别分布图表
        this.categoryChart = echarts.init(this.$refs.categoryChart);
        console.log('积分类别分布图表初始化成功');
        
        // 更新图表数据
        this.updateCharts();
      } catch (error) {
        console.error('图表初始化失败:', error);
        // 失败后延迟再次尝试
        setTimeout(() => {
          if (!this.departmentChart || !this.categoryChart) {
            this.initCharts();
          }
        }, 500);
      }
    },
    
    updateCharts() {
      if (!this.distributionData || !this.distributionData.categories) {
        console.warn('分布数据不完整，无法更新图表:', this.distributionData);
        return;
      }
      
      // 更新部门积分分布图表
      if (this.departmentChart) {
        const departmentOption = {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            }
          },
          legend: {
            data: this.distributionData.series ? this.distributionData.series.map(item => item.name) : []
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: this.distributionData.categories || []
          },
          yAxis: {
            type: 'value'
          },
          series: this.distributionData.series ? this.distributionData.series.map(item => {
            return {
              name: item.name,
              type: 'bar',
              stack: 'total',
              label: {
                show: false
              },
              emphasis: {
                focus: 'series'
              },
              data: item.data || []
            };
          }) : []
        };
        
        try {
          this.departmentChart.setOption(departmentOption);
          console.log('部门积分分布图表更新成功');
        } catch (error) {
          console.error('部门积分分布图表更新失败:', error);
        }
      }
      
      // 更新积分类别分布图表
      if (this.categoryChart) {
        // 为饼图提取或准备数据
        let categoryData = [];
        if (this.distributionData.series && this.distributionData.series.length >= 3) {
          try {
            // 假设series[0]是政治素养，series[1]是专业素养，series[2]是考评及荣誉
            // 计算每个类别的总和
            const politicalTotal = this.distributionData.series[0].data.reduce((sum, val) => sum + val, 0);
            const professionalTotal = this.distributionData.series[1].data.reduce((sum, val) => sum + val, 0);
            const honorTotal = this.distributionData.series[2].data.reduce((sum, val) => sum + val, 0);
            
            categoryData = [
              { name: '政治素养', value: politicalTotal },
              { name: '专业素养', value: professionalTotal },
              { name: '考评及荣誉', value: honorTotal }
            ];
            
            console.log('类别分布数据处理成功:', categoryData);
          } catch (error) {
            console.error('类别分布数据处理失败:', error);
            // 提供默认数据以防出错
            categoryData = [
              { name: '政治素养', value: 1200 },
              { name: '专业素养', value: 1800 },
              { name: '考评及荣誉', value: 1000 }
            ];
          }
        } else {
          console.warn('类别分布数据不完整，使用默认数据');
          categoryData = [
            { name: '政治素养', value: 1200 },
            { name: '专业素养', value: 1800 },
            { name: '考评及荣誉', value: 1000 }
          ];
        }
        
        const categoryOption = {
          tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c} ({d}%)'
          },
          legend: {
            orient: 'vertical',
            left: 10,
            data: categoryData.map(item => item.name)
          },
          series: [
            {
              name: '积分分布',
              type: 'pie',
              radius: ['50%', '70%'],
              avoidLabelOverlap: false,
              label: {
                show: false,
                position: 'center'
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: '18',
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: false
              },
              data: categoryData
            }
          ]
        };
        
        try {
          this.categoryChart.setOption(categoryOption);
          console.log('积分类别分布图表更新成功');
        } catch (error) {
          console.error('积分类别分布图表更新失败:', error);
        }
      }
    },
    
    resizeCharts() {
      if (this.departmentChart) {
        this.departmentChart.resize();
      }
      if (this.categoryChart) {
        this.categoryChart.resize();
      }
    }
  }
};
</script>

<style scoped>
/* 添加自定义样式（如果需要） */
</style> 